import { IStep } from '../../../../../../components/VerticalStepper/VerticalStepper';
import { STEPS_STYLE_NAMES, STEPS_STYLES } from '../../../../../../components/VerticalStepper/VerticalStepper.types';
import { LOGISTIC_SERVICE_STATUS_SEQUENCE } from '../../../../../../constants/history.constants';
import { mapEventsToSubSteps } from '../../../../../../utils/history';
import {
  ILogisticServiceHistory,
  TLogisticServiceStatusName,
  ILogisticServiceHistoryEvent,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../../../types/suscription/history.types';

/**
 * Nombres visibles de los estados del servicio logístico
 */
export const LOGISTIC_SERVICE_STATUS_VISIBLE_NAMES = {
  [LOGISTIC_SERVICE_STATUS_NAME.TO_CONFIRMED]: 'Por confirmar',
  [LOGISTIC_SERVICE_STATUS_NAME.CONFIRMED]: 'Confirmado',
  [LOGISTIC_SERVICE_STATUS_NAME.IN_PROCESS]: 'En proceso',
  [LOGISTIC_SERVICE_STATUS_NAME.ACTIVE]: 'Activo',
  [LOGISTIC_SERVICE_STATUS_NAME.DELIVERED]: 'Entregado',
  [LOGISTIC_SERVICE_STATUS_NAME.CANCELLED]: 'Cancelado',
  [LOGISTIC_SERVICE_STATUS_NAME.DELAYED]: 'Atrasado'
};

interface IStatusStyles {
  titleColor: string;
  icon: React.ReactNode;
  isOpaque: boolean;
}

/**
 * Determina los estilos de cada paso del VerticalStepper para cada estado del historial del servicio logístico
 * @param targetStatus - Estado que se está evaluando
 * @param currentStatus - Estado actual del servicio logístico
 * @returns Objeto con el componente de icono correspondiente, el color del titulo y si el contenido del paso es opaco
 */
export const determineLogisticServiceHistoryStepStyles = (
  targetStatus: TLogisticServiceStatusName,
  currentStatus: TLogisticServiceStatusName
): IStatusStyles => {
  if (
    currentStatus === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED &&
    targetStatus === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED
  ) {
    return STEPS_STYLES[STEPS_STYLE_NAMES.CANCELLED];
  }

  if (
    currentStatus === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED &&
    targetStatus !== LOGISTIC_SERVICE_STATUS_NAME.CANCELLED
  ) {
    return STEPS_STYLES[STEPS_STYLE_NAMES.SECONDARY_CANCELLED];
  }

  if (
    targetStatus === LOGISTIC_SERVICE_STATUS_NAME.DELIVERED &&
    currentStatus === LOGISTIC_SERVICE_STATUS_NAME.DELIVERED
  ) {
    return STEPS_STYLES[STEPS_STYLE_NAMES.SUCCESS];
  }

  const targetStatusIndex = LOGISTIC_SERVICE_STATUS_SEQUENCE.indexOf(targetStatus);
  const currentStatusIndex = LOGISTIC_SERVICE_STATUS_SEQUENCE.indexOf(currentStatus);

  if (targetStatusIndex < currentStatusIndex) return STEPS_STYLES[STEPS_STYLE_NAMES.SUCCESS];
  if (targetStatusIndex === currentStatusIndex) return STEPS_STYLES[STEPS_STYLE_NAMES.IN_PROGRESS];

  return STEPS_STYLES[STEPS_STYLE_NAMES.WAITING];
};

/**
 * Obtiene los eventos del historial del servicio logístico para un estado específico
 * @param history - Historial del servicio logístico
 * @param statusName - Estado que se está evaluando
 * @returns Los eventos para el estado específico
 */
export const getEventsForStatus = (
  history: ILogisticServiceHistory[],
  statusName: TLogisticServiceStatusName
): ILogisticServiceHistoryEvent[] => {
  return history.find(history => history.name === statusName)?.events || [];
};

/**
 * Transforma el historial del servicio logístico a pasos del componente VerticalStepper
 * @param history - Historial del servicio logístico
 * @param currentStatus - Estado actual del servicio logístico
 * @returns Los pasos del historial del servicio logístico transformados a pasos del componente VerticalStepper
 */
export const transformLogisticServiceHistoryToStepperSteps = (
  history: ILogisticServiceHistory[],
  currentStatus: TLogisticServiceStatusName
): IStep[] => {
  if (history.length === 0) {
    return [];
  }

  const steps = LOGISTIC_SERVICE_STATUS_SEQUENCE.map(status => {
    const { titleColor, icon, isOpaque } = determineLogisticServiceHistoryStepStyles(status, currentStatus);
    const subSteps = mapEventsToSubSteps(getEventsForStatus(history, status));

    return {
      title: LOGISTIC_SERVICE_STATUS_VISIBLE_NAMES[status],
      titleColor,
      icon,
      subSteps,
      isOpaque
    };
  }) as IStep[];

  if (currentStatus === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED) {
    const stepsWithSubSteps = steps.filter(step => step.subSteps && step.subSteps.length > 0);

    const { titleColor, icon, isOpaque } = determineLogisticServiceHistoryStepStyles(currentStatus, currentStatus);
    const subSteps = mapEventsToSubSteps(getEventsForStatus(history, currentStatus));

    const finalStepsWithCancelStep = stepsWithSubSteps.concat({
      title: LOGISTIC_SERVICE_STATUS_VISIBLE_NAMES[currentStatus],
      titleColor,
      icon,
      subSteps,
      isOpaque
    });

    return finalStepsWithCancelStep;
  }

  return steps;
};
